import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useT } from '@/i18n/labels';
import { useThemeStore } from '@/stores/themeStore';
import { dirFor } from '@/stores/langStore';
import { makeTokens } from '@/theme/tokens';

/**
 * Sign-in hand-off landing page (PUBLIC / pre-session) at /auth/handoff.
 *
 * The marketing site's /login checked the agent's company code, email and password and sent the
 * browser here with a single-use code in the URL FRAGMENT: /auth/handoff#code=..&tenant=..
 * A fragment never reaches a server and never leaves in a Referer. This page reads it, strips it
 * from the address bar and history at once, and exchanges it for a session from this origin (the
 * server uses that origin to allow only an Agent Console code here). The session is stored exactly
 * as login() stores one, refresh timer included. Anything missing or refused goes to /login with an
 * "expired" notice and the company code prefilled. Same flow as admin-portal's HandoffPage.
 */
export const HandoffPage: React.FC = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { completeHandoff } = useAuth();
  const mode = useThemeStore((s) => s.mode);
  const tokens = useMemo(() => makeTokens({ mode }), [mode]);
  // StrictMode runs effects twice in development; a code is single-use, so post it once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const code = params.get('code');
    const tenant = params.get('tenant');
    window.history.replaceState(window.history.state, '', window.location.pathname);

    const toLogin = () =>
      navigate(tenant ? `/login?tenant=${encodeURIComponent(tenant)}&handoff=expired` : '/login?handoff=expired', {
        replace: true,
      });

    if (!code || !tenant) {
      toLogin();
      return;
    }
    completeHandoff(code, tenant)
      .then(() => navigate('/', { replace: true }))
      .catch(toLogin);
  }, [completeHandoff, navigate]);

  return (
    <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
      <div className="ac-card ac-login-card" role="status" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <LoadingSpinner size={18} />
        {t('handoffSigningIn')}
      </div>
    </div>
  );
};
